import { ArrowCounterClockwise, BookmarkSimple, CaretDown } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import type { ViewDto } from "@plannello/shared";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import type { ViewSearch } from "@/components/items/viewSearch";
import { Button, IconButton, Menu, MenuItem, MenuTrigger } from "@/components/ui";
import type { Product } from "@/data/types";
import { pushUndo } from "@/data/undo";
import { toast } from "@/lib/toast";
import { patchView, useProductViews } from "./api";
import { openSaveView, viewName } from "./viewMeta";
import { hasCustomState, type ItemLayout, isDirty, searchToDisplay, viewLink } from "./viewState";

/** The saved view named by `?view=` (undefined while loading or when it is gone). */
export function useLoadedView(productKey: string, viewId: string | undefined): ViewDto | undefined {
  const q = useProductViews(viewId ? productKey : undefined);
  return viewId ? q.data?.find((v) => v.id === viewId) : undefined;
}

/**
 * What the current URL state means for views. A loaded view that changed shows "Edited" with
 * Save (when you may edit it), Save as new view and Reset; without a loaded view, any custom
 * filter or display state offers "Save view". Saving keeps an undo.
 */
export function ViewControls({
  product,
  layout,
  search,
}: {
  product: Product;
  layout: ItemLayout;
  search: ViewSearch;
}) {
  const navigate = useNavigate();
  const view = useLoadedView(product.key, search.view);
  const [saving, setSaving] = useState(false);

  if (view) {
    if (!isDirty(view, layout, search)) return null;
    const name = viewName(view);
    const reset = () => {
      const link = viewLink(view, product.key);
      void navigate({ to: link.to, params: link.params, search: link.search as never, replace: true });
    };
    const save = async () => {
      const before = { query: view.query, layout: view.layout, display: view.display };
      setSaving(true);
      try {
        await patchView(view.id, {
          query: (search.q ?? "").trim(),
          layout,
          display: searchToDisplay(search, product.key),
        });
        toast.undo(
          m.views_updated({ name }),
          pushUndo(() => void patchView(view.id, before).catch(() => undefined)),
          m.common_undo(),
        );
      } catch {
        toast.show({ title: m.toast_update_failed(), tone: "danger" });
      } finally {
        setSaving(false);
      }
    };
    return (
      // biome-ignore lint/a11y/useSemanticElements: a status group, not a form fieldset
      <div role="group" aria-label={m.views_changes({ name })} className="flex items-center gap-1">
        <span
          role="status"
          className="inline-flex h-5 items-center rounded-mark border border-line px-1.5 text-xs font-medium text-fg-muted"
        >
          {m.views_edited()}
        </span>
        <IconButton aria-label={m.views_reset({ name })} icon={ArrowCounterClockwise} size="sm" onPress={reset} />
        {view.canEdit ? (
          <div className="flex items-center">
            <Button
              variant="secondary"
              size="sm"
              isPending={saving}
              onPress={() => void save()}
              className="rounded-r-none"
            >
              {m.common_save()}
            </Button>
            <MenuTrigger>
              <IconButton
                aria-label={m.views_more_save()}
                icon={CaretDown}
                size="sm"
                variant="secondary"
                iconSize={12}
                className="-ml-px rounded-l-none"
              />
              <Menu aria-label={m.views_more_save()} placement="bottom end">
                <MenuItem id="save-as" onAction={() => openSaveView({ mode: "create" })}>
                  {m.views_save_as()}
                </MenuItem>
              </Menu>
            </MenuTrigger>
          </div>
        ) : (
          <Button variant="secondary" size="sm" onPress={() => openSaveView({ mode: "create" })}>
            {m.views_save_as()}
          </Button>
        )}
      </div>
    );
  }

  if (!hasCustomState(layout, search)) return null;
  return (
    <Button variant="ghost" size="sm" onPress={() => openSaveView({ mode: "create" })}>
      <BookmarkSimple size={14} aria-hidden="true" />
      {m.views_save()}
    </Button>
  );
}
